/**
 * Pure guard function for the "auto-create new user on first login" path.
 *
 * --------------------------------------------------------------------------
 * WHY THIS FILE EXISTS (for code reviewers asking "is this overkill?"):
 * --------------------------------------------------------------------------
 *
 * Same pattern as `buildScheduleUpdateFields`, `datesEqual`,
 * `buildDragNDropUpdateFields`, `computeJobInitValues`, and
 * `performLogoutClears`: extract the critical conditional logic into a
 * single pure function so tests import the EXACT production code. If a
 * future change accidentally drops any clause of this guard — especially
 * the `!hasTriedCreateUser` clause, which is the #90 fix — the regression
 * tests fail. No test-helper drift.
 *
 * --------------------------------------------------------------------------
 * THE BUG THIS HELPS PREVENT (#90):
 * --------------------------------------------------------------------------
 *
 * Before this fix, the auto-create useEffect in modelCx.context.js called
 * `createNewUser.mutate()` whenever five conditions were true: query not
 * fetching, userResult empty, mongoUser has id+email, and mutation not
 * currently pending.
 *
 * The race: after mutation completes, createNewUser.isPending → false. The
 * getUserByEmail refetch is triggered by setShouldFetchUser(true) in
 * onSuccess, but doesn't complete synchronously. In the window between
 * "mutation done" and "query cache updated with new user doc," any
 * dependency change (mongoUser reference churn, polling tick, etc.)
 * re-ran the useEffect, all five conditions evaluated true again, and
 * createNewUser fired AGAIN. Every fire inserted another duplicate.
 *
 * Result in production as of 2026-04-21:
 *   - 28,112 documents in Users collection
 *   - 176 distinct usernames
 *   - Top user had 2,017 duplicate copies
 *   - 113 of 176 users affected
 *
 * The fix adds a sixth clause: `!hasTriedCreateUser`. Once we fire
 * createNewUser ONCE per session, we never fire it again until logout
 * (when performLogoutClears resets the ref). Database-level uniqueness
 * is NOT relied on — the frontend alone prevents the race.
 */

/**
 * Returns true if the conditions for firing createNewUser.mutate are met.
 *
 * All six clauses must pass:
 *   1. The getUserByEmail query is not currently fetching (we have a real
 *      empty result, not a pending result)
 *   2. The userResult from the query is null / empty object (user truly
 *      not in the DB)
 *   3. The MSAL mongoUser has an `id` (so we can write it to the new doc)
 *   4. The MSAL mongoUser has an `email` (so we have a username)
 *   5. The createNewUser mutation is not currently in flight
 *   6. #90 GUARD: we have not already tried creating a user this session.
 *      Without this clause, the race described above produced ~27,936
 *      duplicate documents in production.
 *
 * @param {Object} params
 * @param {boolean} params.userFetching - TanStack Query's `isFetching` flag
 *   for getUserByEmail
 * @param {Object|null|undefined} params.userResult - Result from
 *   getUserByEmail (null, undefined, {}, or a populated user document)
 * @param {Object|null|undefined} params.mongoUser - The MSAL-decoded user
 *   object. Expected shape: { id, _profile: { data: { email } } }
 * @param {boolean} params.createNewUserIsPending - TanStack Query's
 *   `isPending` flag on the createNewUser mutation
 * @param {boolean} params.hasTriedCreateUser - Session-scoped ref value:
 *   has the auto-create path already fired once this session?
 * @returns {boolean} true if and only if all 6 conditions pass
 */
export function shouldCreateNewUser({
  userFetching,
  userResult,
  mongoUser,
  createNewUserIsPending,
  hasTriedCreateUser,
}) {
  if (userFetching) return false;

  const userResultIsEmpty =
    !userResult || Object.keys(userResult).length === 0;
  if (!userResultIsEmpty) return false;

  if (!mongoUser?.id) return false;
  if (!mongoUser?._profile?.data?.email) return false;

  if (createNewUserIsPending) return false;

  // #90 LOAD-BEARING GUARD. Do not remove without also changing the
  // shouldCreateNewUser.test.js regression tests.
  if (hasTriedCreateUser) return false;

  return true;
}
